'use client';

import { useState, useEffect } from 'react';
import { Calendar, MessageSquare, User, Plus, Trash2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';

interface Goal {
  id: string;
  goal_type: 'frequency' | 'topic' | 'speaker';
  goal_config: Record<string, unknown>;
  progress: Record<string, unknown>;
  is_active: boolean;
  priority: number;
}

interface GoalSettingFormProps {
  domainId: string;
  onClose: () => void;
}

export function GoalSettingForm({ domainId, onClose }: GoalSettingFormProps) {
  const [goals, setGoals] = useState<Goal[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'frequency' | 'topic' | 'speaker'>('frequency');

  const [frequencyForm, setFrequencyForm] = useState({
    platform: 'linkedin',
    count: 2,
    period: 'week' as 'week' | 'month',
  });

  const [topicForm, setTopicForm] = useState({
    theme: '',
    keywords: [] as string[],
    keywordInput: '',
  });

  const [speakerForm, setSpeakerForm] = useState({
    speakerName: '',
    intent: 'feature_more' as 'feature_more' | 'establish_expert',
  });

  useEffect(() => {
    const fetchGoals = async () => {
      try {
        const res = await fetch(`/api/momento/goals?domainId=${domainId}`);
        if (res.ok) {
          const data = await res.json();
          setGoals(data.goals || []);
        }
      } catch (error) {
        console.error('[GoalSettingForm] Failed to fetch goals:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchGoals();
  }, [domainId]);

  const handleAddGoal = async () => {
    setIsSaving(true);

    let goalConfig: Record<string, unknown> = {};

    switch (activeTab) {
      case 'frequency':
        goalConfig = frequencyForm;
        break;
      case 'topic':
        goalConfig = {
          theme: topicForm.theme,
          keywords: topicForm.keywords,
        };
        break;
      case 'speaker':
        goalConfig = speakerForm;
        break;
    }

    try {
      const res = await fetch('/api/momento/goals', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          domainId,
          goal_type: activeTab,
          goal_config: goalConfig,
          priority: goals.length,
        }),
      });

      if (res.ok) {
        const data = await res.json();
        setGoals([...goals, data.goal]);

        if (activeTab === 'topic') {
          setTopicForm({ theme: '', keywords: [], keywordInput: '' });
        } else if (activeTab === 'speaker') {
          setSpeakerForm({ speakerName: '', intent: 'feature_more' });
        }
      }
    } catch (error) {
      console.error('[GoalSettingForm] Failed to add goal:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteGoal = async (goalId: string) => {
    try {
      const res = await fetch(`/api/momento/goals/${goalId}`, {
        method: 'DELETE',
      });

      if (res.ok) {
        setGoals(goals.filter(g => g.id !== goalId));
      }
    } catch (error) {
      console.error('[GoalSettingForm] Failed to delete goal:', error);
    }
  };

  const addKeyword = () => {
    if (topicForm.keywordInput.trim()) {
      setTopicForm({
        ...topicForm,
        keywords: [...topicForm.keywords, topicForm.keywordInput.trim()],
        keywordInput: '',
      });
    }
  };

  const removeKeyword = (index: number) => {
    setTopicForm({
      ...topicForm,
      keywords: topicForm.keywords.filter((_, i) => i !== index),
    });
  };

  const getGoalSummary = (goal: Goal) => {
    const config = goal.goal_config;
    switch (goal.goal_type) {
      case 'frequency':
        return `${config.count}x per ${config.period} on ${config.platform}`;
      case 'topic':
        return `"${config.theme}" theme`;
      case 'speaker':
        return `${config.intent === 'establish_expert' ? 'Establish' : 'Feature'} ${config.speakerName}`;
      default:
        return 'Custom goal';
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-sm">Content Goals</h3>
        <Button variant="ghost" size="sm" onClick={onClose} className="h-6 px-2 text-xs">
          Done
        </Button>
      </div>

      {goals.length > 0 && (
        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground">Active Goals</Label>
          <div className="space-y-1.5">
            {goals.map(goal => (
              <div
                key={goal.id}
                className="flex items-center justify-between p-2 rounded-lg bg-muted/50 text-sm"
              >
                <div className="flex items-center gap-2">
                  {goal.goal_type === 'frequency' && <Calendar className="w-3.5 h-3.5 text-purple-500" />}
                  {goal.goal_type === 'topic' && <MessageSquare className="w-3.5 h-3.5 text-blue-500" />}
                  {goal.goal_type === 'speaker' && <User className="w-3.5 h-3.5 text-green-500" />}
                  <span className="text-xs">{getGoalSummary(goal)}</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDeleteGoal(goal.id)}
                  className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="border-t pt-4">
        <Label className="text-xs text-muted-foreground mb-2 block">Add New Goal</Label>

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as typeof activeTab)}>
          <TabsList className="grid w-full grid-cols-3 h-8">
            <TabsTrigger value="frequency" className="text-xs">
              <Calendar className="w-3 h-3 mr-1" />
              Frequency
            </TabsTrigger>
            <TabsTrigger value="topic" className="text-xs">
              <MessageSquare className="w-3 h-3 mr-1" />
              Topic
            </TabsTrigger>
            <TabsTrigger value="speaker" className="text-xs">
              <User className="w-3 h-3 mr-1" />
              Speaker
            </TabsTrigger>
          </TabsList>

          <TabsContent value="frequency" className="space-y-3 mt-3">
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-xs">Count</Label>
                <Input
                  type="number"
                  min={1}
                  max={20}
                  value={frequencyForm.count}
                  onChange={(e) => setFrequencyForm({ ...frequencyForm, count: parseInt(e.target.value) || 1 })}
                  className="h-8 text-sm"
                />
              </div>
              <div>
                <Label className="text-xs">Period</Label>
                <Select
                  value={frequencyForm.period}
                  onValueChange={(v) => setFrequencyForm({ ...frequencyForm, period: v as 'week' | 'month' })}
                >
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="week">Week</SelectItem>
                    <SelectItem value="month">Month</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Platform</Label>
                <Select
                  value={frequencyForm.platform}
                  onValueChange={(v) => setFrequencyForm({ ...frequencyForm, platform: v })}
                >
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="linkedin">LinkedIn</SelectItem>
                    <SelectItem value="twitter">Twitter/X</SelectItem>
                    <SelectItem value="any">Any</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="topic" className="space-y-3 mt-3">
            <div>
              <Label className="text-xs">Theme</Label>
              <Input
                placeholder="e.g., AI investing, product leadership"
                value={topicForm.theme}
                onChange={(e) => setTopicForm({ ...topicForm, theme: e.target.value })}
                className="h-8 text-sm"
              />
            </div>
            <div>
              <Label className="text-xs">Keywords (optional)</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="Add keyword"
                  value={topicForm.keywordInput}
                  onChange={(e) => setTopicForm({ ...topicForm, keywordInput: e.target.value })}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addKeyword())}
                  className="h-8 text-sm flex-1"
                />
                <Button type="button" variant="outline" size="sm" onClick={addKeyword} className="h-8">
                  <Plus className="w-3 h-3" />
                </Button>
              </div>
              {topicForm.keywords.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {topicForm.keywords.map((kw, i) => (
                    <Badge
                      key={i}
                      variant="secondary"
                      className="text-xs cursor-pointer hover:bg-destructive hover:text-destructive-foreground"
                      onClick={() => removeKeyword(i)}
                    >
                      {kw} &times;
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="speaker" className="space-y-3 mt-3">
            <div>
              <Label className="text-xs">Speaker Name</Label>
              <Input
                placeholder="e.g., Camilo, Sarah Chen"
                value={speakerForm.speakerName}
                onChange={(e) => setSpeakerForm({ ...speakerForm, speakerName: e.target.value })}
                className="h-8 text-sm"
              />
            </div>
            <div>
              <Label className="text-xs">Intent</Label>
              <Select
                value={speakerForm.intent}
                onValueChange={(v) => setSpeakerForm({ ...speakerForm, intent: v as typeof speakerForm.intent })}
              >
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="feature_more">Feature more content</SelectItem>
                  <SelectItem value="establish_expert">Establish as expert</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </TabsContent>
        </Tabs>

        <Button
          onClick={handleAddGoal}
          disabled={isSaving || (activeTab === 'topic' && !topicForm.theme) || (activeTab === 'speaker' && !speakerForm.speakerName)}
          className="w-full mt-4 h-8 text-sm bg-purple-500 hover:bg-purple-600"
        >
          {isSaving ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              <Plus className="w-3.5 h-3.5 mr-1" />
              Add Goal
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
